/**
 * Stepper — shared wizard step indicator (Variant 1: refined rings,
 * label to the right of dot).
 *
 * Visual contract (matches stepper-mockup.html .v1):
 *   - 30×30 dot, 1.5px ring at rest, mono numeral in border-strong gray
 *   - done: gradient primary→brand fill, white check icon, soft drop shadow
 *   - active: white surface, brand ring, brand numeral, 4px brand-tinted halo
 *   - label: Lato 14, weight 600 (700 on active), color steps text-3 → 2 → 1
 *   - connector: 2px line, fills with gradient primary→brand once passed
 *
 * API (unchanged from MUI-based predecessor — drop-in replacement so
 * all consumers — CreatePatientPanel, CreateAssessmentPanel,
 * SendResourceInvitationPanel — pick up the new look automatically):
 *   <Stepper
 *     steps={[{ id, label }, ...]}
 *     activeStep={stepId}
 *     visitedSteps={Set<id>}        // optional → drives done state
 *     onJumpTo={(stepId) => void}   // optional → done steps clickable
 *   />
 *
 * No MUI dependency. The previous wrapper used MUI Stepper because
 * features/* are blocked from importing @mui/* directly; this file
 * lives in @shared/ui so it could, but with the layout requirements
 * being minimal (flex row + line spacers), the custom build is simpler
 * and cheaper than fighting MUI's StepConnector positioning.
 */
import { Fragment } from 'react';
import { styled } from '../styled';

const Wrap = styled('nav')({
  display: 'flex',
  alignItems: 'center',
  gap: 0,
});

const Step = styled('div', { shouldForwardProp: p => p !== '$clickable' })(({ $clickable }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flex: '0 0 auto',
  position: 'relative',
  zIndex: 1,
  cursor: $clickable ? 'pointer' : 'default',
  border: 'none',
  background: 'transparent',
  padding: 0,
  appearance: 'none',
  textAlign: 'left',
}));

const DOT_PALETTE = {
  upcoming: {
    background: 'var(--qhs-surface)',
    border: '1.5px solid var(--qhs-border-strong)',
    color: 'var(--qhs-text-3)',
    shadow: 'none',
  },
  done: {
    background: 'linear-gradient(135deg, var(--qhs-primary), var(--qhs-brand))',
    border: '1.5px solid transparent',
    color: '#fff',
    shadow: '0 1px 3px rgba(46, 72, 211, 0.25)',
  },
  active: {
    background: 'var(--qhs-surface)',
    border: '1.5px solid var(--qhs-brand)',
    color: 'var(--qhs-brand)',
    shadow: '0 0 0 4px rgba(46, 72, 211, 0.12), 0 1px 3px rgba(46, 72, 211, 0.18)',
  },
};

const Dot = styled('span', { shouldForwardProp: p => p !== '$state' })(({ $state }) => {
  const p = DOT_PALETTE[$state];
  return {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: p.background,
    border: p.border,
    color: p.color,
    boxShadow: p.shadow,
    display: 'grid',
    placeItems: 'center',
    fontFamily: "'Source Code Pro', 'JetBrains Mono', monospace",
    fontSize: 13,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
    transition:
      'background-color 200ms ease, border-color 200ms ease, color 200ms ease, box-shadow 200ms ease',
  };
});

const Label = styled('span', { shouldForwardProp: p => p !== '$state' })(({ $state }) => ({
  fontFamily: 'var(--qhs-font-head)',
  fontSize: 14,
  fontWeight: $state === 'active' ? 700 : 600,
  letterSpacing: '-0.05px',
  whiteSpace: 'nowrap',
  color:
    $state === 'active'
      ? 'var(--qhs-text-1)'
      : $state === 'done'
        ? 'var(--qhs-text-2)'
        : 'var(--qhs-text-3)',
  transition: 'color 200ms ease',
}));

const Line = styled('span', { shouldForwardProp: p => p !== '$filled' })(({ $filled }) => ({
  flex: 1,
  height: 2,
  minWidth: 32,
  borderRadius: 2,
  margin: '0 16px',
  background: $filled
    ? 'linear-gradient(90deg, var(--qhs-primary), var(--qhs-brand))'
    : 'var(--qhs-border)',
  transition: 'background 200ms ease',
}));

const CheckIcon = () => (
  <svg
    viewBox='0 0 16 16'
    width='14'
    height='14'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.4'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <polyline points='3.5 8.5 6.5 11.5 12.5 5' />
  </svg>
);

const Stepper = ({ steps, activeStep, visitedSteps, onJumpTo }) => {
  if (!steps?.length) return null;

  const activeIndex = Math.max(
    0,
    steps.findIndex(s => s.id === activeStep)
  );

  // Done state covers two cases: (a) any step before the active one
  // (positional), and (b) any step in visitedSteps that's not currently
  // active (explicit, supports forward-jumping flows where the panel
  // lets you revisit completed steps even after moving past them).
  const isDone = (idx, stepId) =>
    idx < activeIndex || (!!visitedSteps && visitedSteps.has(stepId) && stepId !== activeStep);

  const stateOf = (idx, step) => {
    if (step.id === activeStep) return 'active';
    if (isDone(idx, step.id)) return 'done';
    return 'upcoming';
  };

  return (
    <Wrap aria-label='Progress'>
      {steps.map((step, idx) => {
        const state = stateOf(idx, step);
        const clickable = state === 'done' && typeof onJumpTo === 'function';
        const handleClick = clickable ? () => onJumpTo(step.id) : undefined;
        const StepEl = clickable ? Step.withComponent('button') : Step;
        return (
          <Fragment key={step.id}>
            <StepEl
              $clickable={!!clickable}
              onClick={handleClick}
              type={clickable ? 'button' : undefined}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <Dot $state={state}>{state === 'done' ? <CheckIcon /> : idx + 1}</Dot>
              <Label $state={state}>{step.label}</Label>
            </StepEl>
            {idx < steps.length - 1 && <Line $filled={idx < activeIndex} />}
          </Fragment>
        );
      })}
    </Wrap>
  );
};

export default Stepper;
